import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { Link } from 'react-router-dom'
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import Tab from '@mui/material/Tab';
import { getDollarFormat, roundToDecimalPlace } from 'utils/helpers';
import NoDataMsg from 'library/noData';
// Child Component
import TabGrid from './TabGrid';

const Body = ({ tabData }) => {
    const { details, branchSummary, summary } = tabData || {};
    // Redux State
    const {
        DiscountReportBeginDate: beginDate,
        DiscountReportEndDate: endDate,
    } = useSelector(state => state.form || {});

    // Local Stated
    const [currentTab, setCurrentTab] = useState("manual");
    const onChangeTab = (e, value) => setCurrentTab(value);

    const dateSignCell = ({ value }) => <span>{moment(value, "YYYY/MM/DD").format("YYYY-MMM-DD")}</span>;
    const dollarSignCell = ({ value }) => <span>{getDollarFormat(value)}</span>;
    const orderIdCell = ({ value }) => <Link to={`/admin/orders/detail/${value}`}>{value}</Link>;
    const nameCell = ({ value, data }) => <span>{`${value} ${data.employeeLastName}`}</span>;
    const pnameCell = ({ value, data }) => <span>{`${value} ${data.patientLastName}`}</span>;
    const percentageCell = ({ value, data }) => <span>{`${Math.round(value / data.originalPrice * 100)}%`}</span>;
    const orderTypeCell = ({ value }) => <span>{`${value === 1 ? "In Store" : "Online"}`}</span>;
    const campaignNameCell = ({ value }) => <span>{`${value || `Campaign deleted`}`}</span>;

    const columnManual = useMemo(() =>  [
        { headerName: "Date", field: "date", sortable: true, width: 120, cellRenderer: dateSignCell },
        { headerName: "Branch", field: "branchName", sortable: true, width: 200 },
        { headerName: "OrderId", field: "orderId", sortable: true, width: 100, cellRenderer: orderIdCell },
        { headerName: "Employee Name", field: "employeeFirstName", sortable: true, width: 150, cellRenderer: nameCell },
        { headerName: "Original Price", field: "originalPrice", sortable: true, width: 100, cellRenderer: dollarSignCell },
        { headerName: "Discount Total", field: "discounts", sortable: true, width: 100, cellRenderer: dollarSignCell },
        { headerName: "Discount %", field: "discounts", sortable: true, width: 140, cellRenderer: percentageCell },
        { headerName: "Order Type", field: "orderType", sortable: true, width: 100, cellRenderer: orderTypeCell },
        { headerName: "Notes", field: "discountNotes", sortable: true, width: 100, cellRenderer: dollarSignCell },
        { headerName: "Price Match", field: "priceMatchPlus", sortable: true, width: 80 },
    ], []);

    const columnCustomized = useMemo(() =>  [
        { headerName: "Date", field: "date", sortable: true, width: 120, cellRenderer: dateSignCell },
        { headerName: "Branch", field: "branchName", sortable: true, width: 200 },
        { headerName: "OrderId", field: "orderId", sortable: true, width: 100, cellRenderer: orderIdCell },
        { headerName: "Customized Name", field: "customizedDiscountName", sortable: true, width: 150 },
        { headerName: "Employee Name", field: "employeeFirstName", sortable: true, width: 150, cellRenderer: nameCell },
        { headerName: "Original Price", field: "originalPrice", sortable: true, width: 100, cellRenderer: dollarSignCell },
        { headerName: "Discount Total", field: "discounts", sortable: true, width: 100, cellRenderer: dollarSignCell },
        { headerName: "Discount %", field: "discounts", sortable: true, width: 140, cellRenderer: percentageCell },
        { headerName: "Order Type", field: "orderType", sortable: true, width: 100, cellRenderer: orderTypeCell },
    ], []);

    const columnCampaign = useMemo(() =>  [
        { headerName: "Date", field: "date", sortable: true, width: 120, cellRenderer: dateSignCell },
        { headerName: "Branch", field: "branchName", sortable: true, width: 200 },
        { headerName: "OrderId", field: "orderId", sortable: true, width: 100, cellRenderer: orderIdCell },
        { headerName: "CampaignId", field: "discountReasonId", sortable: true, width: 150 },
        { headerName: "CampaignName", field: "discountReasonId", sortable: true, width: 150, cellRenderer: campaignNameCell },
        { headerName: "Employee Name", field: "employeeFirstName", sortable: true, width: 150, cellRenderer: nameCell },
        { headerName: "Original Price", field: "originalPrice", sortable: true, width: 100, cellRenderer: dollarSignCell },
        { headerName: "Discount Total", field: "discounts", sortable: true, width: 100, cellRenderer: dollarSignCell },
        { headerName: "Discount %", field: "discounts", sortable: true, width: 140, cellRenderer: percentageCell },
        { headerName: "Order Type", field: "orderType", sortable: true, width: 100, cellRenderer: orderTypeCell },
    ], []);

    const columnLoyalty = useMemo(() =>  [
        { headerName: "Date", field: "date", sortable: true, width: 120, cellRenderer: dateSignCell },
        { headerName: "Branch", field: "branchName", sortable: true, width: 200 },
        { headerName: "OrderId", field: "orderId", sortable: true, width: 100, cellRenderer: orderIdCell },
        { headerName: "Employee Name", field: "employeeFirstName", sortable: true, width: 150, cellRenderer: nameCell },
        { headerName: "PatientId", field: "patientId", sortable: true, width: 150 },
        { headerName: "PatientName", field: "patientFirstName", sortable: true, width: 150, cellRenderer: pnameCell },
        { headerName: "Original Price", field: "originalPrice", sortable: true, width: 100, cellRenderer: dollarSignCell },
        { headerName: "Discount Total", field: "discounts", sortable: true, width: 100, cellRenderer: dollarSignCell },
        { headerName: "Discount %", field: "discounts", sortable: true, width: 140, cellRenderer: percentageCell },
        { headerName: "Points Used", field: "rewardPointsUsed", sortable: true, width: 100, cellRenderer: orderTypeCell },
    ], []);

    const memoRowsManual = useMemo(() => details?.manual || [], [details?.manual]);
    const memoRowsCustomized = useMemo(() => details?.customized || [], [details?.customized]);
    const memoRowsCampaign = useMemo(() => details?.campaign || [], [details?.campaign]);
    const memoRowsLoyalty = useMemo(() => details?.loyalty || [], [details?.loyalty]);
    const memoBranchSummary = useMemo(() => branchSummary || [], [branchSummary]);

    return(
        <React.Fragment>
            {!details
                ?
                    //If there's NO DATA to display
                    <NoDataMsg />
                    :
                    details && summary &&
                    //If there's DATA
                    <div>
                        <div className="row m-0">
                            <div className="text-center list_header">
                                {beginDate ?
                                    <p><i>From</i> <b>{moment(beginDate).format('MMMM Do, YYYY')}</b> <i> to </i>
                                        {endDate ? <b>{moment(endDate).format('MMMM Do, YYYY')}</b> : moment().format('MMMM Do, YYYY')}</p>
                                    : ""}
                            </div>
                        </div>
                        <div className="DiscountReportBody">
                            <div className="col-lg-12 title_summary">
                                <h3 className="m-0 text-center text-justify">SUMMARY</h3>
                            </div>
                            <TabContext id="discount-report" value={currentTab}>
                                <TabList onChange={onChangeTab} aria-label="lab API tabs example" variant="fullWidth">
                                    <Tab label={<div className="text-center">
                                        <label>Manual Discount</label>
                                        <h4>{getDollarFormat(summary.manual)}</h4>
                                    </div>} value="manual" />
                                    <Tab label={<div className="text-center">
                                        <label>Customized Discount</label>
                                        <h4>{getDollarFormat(summary.customized)}</h4>
                                    </div>} value="customized" />
                                    <Tab label={<div className="text-center">
                                        <label>Campaign</label>
                                        <h4>{getDollarFormat(summary.campaign)}</h4>
                                    </div>} value="campaign" />
                                    <Tab label={<div className="text-center">
                                        <label>Loyalty Discount</label>
                                        <h4>{getDollarFormat(summary.loyalty)}</h4>
                                    </div>} value="loyalty" />
                                </TabList>
                                <TabGrid value="manual" rows={memoRowsManual} branchSummary={memoBranchSummary} columns={columnManual} />
                                <TabGrid value="customized" rows={memoRowsCustomized} branchSummary={memoBranchSummary} columns={columnCustomized} />
                                <TabGrid value="campaign" rows={memoRowsCampaign} branchSummary={memoBranchSummary} columns={columnCampaign} />
                                <TabGrid value="loyalty" rows={memoRowsLoyalty} branchSummary={memoBranchSummary} columns={columnLoyalty} />
                            </TabContext>
                        </div>
                    </div>
            }
        </React.Fragment>
    );
}

export default Body;


